import { faClone, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { Button, Icon, TextInput } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import React, { HTMLAttributes, ReactNode } from 'react'

import styles from './list.module.scss'

export function EditPlannerList<T = string | object>({
  label,
  placeholder,
  items,
  onAdd,
  onDelete,
  onEdit,
  onMove,
  onClone,
  renderer,
  className,
  children,
  ...props
}: {
  label?: ReactNode
  placeholder?: string
  items: T[]
  onAdd?: () => void
  onDelete?: (index: number, event: React.MouseEvent) => void
  onEdit?: (index: number, newValue: T) => void
  onMove?: (sourceIndex: number, destinationIndex: number) => void
  onClone?: (index: number, event: React.MouseEvent) => void
  renderer?: ({ item, index }: { item: T; index: number }) => ReactNode
} & HTMLAttributes<HTMLDivElement>) {
  const onDragEnd = React.useCallback(
    ({ destination, source }: DropResult) => {
      if (!destination || source.index === destination.index) return
      onMove?.(source.index, destination.index)
    },
    [onMove]
  )

  return (
    <div className={classNames(styles.List, className)} {...props}>
      {label && <span className={styles.List__subtitle}>{label}</span>}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="planner-list">
          {(provided) => (
            <ul className={styles.List__list} {...provided.droppableProps} ref={provided.innerRef}>
              {items.map((item, index) => (
                <Draggable key={index} draggableId={index.toString()} isDragDisabled={!onMove} index={index}>
                  {(provided) => (
                    <li {...provided.draggableProps} ref={provided.innerRef}>
                      {onMove && (
                        <div {...provided.dragHandleProps}>
                          <Icon className={styles.List__dragIcon} icon="icons/drag-2.svg" width={11} height={15} />
                        </div>
                      )}
                      {renderer ? (
                        renderer({ item, index })
                      ) : (
                        <TextInput
                          size="sm-md"
                          placeholder={placeholder || 'Enter text'}
                          disabled={!onEdit}
                          value={item as string}
                          onChange={(newValue) => {
                            onEdit?.(index, newValue as T)
                          }}
                        />
                      )}
                      {onClone && (
                        <FontAwesomeIcon
                          icon={faClone}
                          className={styles.List__cloneIcon}
                          onClick={(e) => {
                            onClone(index, e)
                          }}
                        />
                      )}
                      <FontAwesomeIcon
                        icon={faTrashAlt}
                        className={classNames(styles.List__deleteIcon, !onDelete && styles.List__deleteIcon)}
                        onClick={(e) => {
                          onDelete?.(index, e)
                        }}
                      />
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>

      <div className={styles.ListButtons}>
        <Button
          disabled={!onAdd}
          onClick={() => {
            onAdd?.()
          }}>
          Add
        </Button>
        {children}
      </div>
    </div>
  )
}
